<template>
    <div class="missed-call-handler-settings" v-if="consent.is_applicable">
        <el-card class="mb-4">
            <div class="card-header d-flex align-items-center justify-content-between">
                <h4 class="mb-0">
                    AI Call Rescue (Beta)
                </h4>
                <el-tooltip
                    :content="handlerState === $options.HANDLER_STATE_NO_CONSENT ? 'Consent required before enabling' : ''"
                    :disabled="handlerState !== $options.HANDLER_STATE_NO_CONSENT"
                    placement="left"
                >
                    <el-switch
                        v-model="enabled"
                        active-color="#00a344"
                        @change="handleToggle"
                        :disabled="loading || handlerState === $options.HANDLER_STATE_NO_CONSENT"
                        size="large"
                    />
                </el-tooltip>
            </div>

            <div class="card-content">
                <!-- STATE 1: ENABLED - Consent accepted and feature is enabled -->
                <div v-if="handlerState === $options.HANDLER_STATE_ENABLED">
                    <p class="content-text">
                        <strong>AloAi is answering all your missed calls.</strong>
                    </p>
                    
                    <p class="content-text">
                        Lines and Inboxes are now handled by AI Call Rescue.
                    </p>
                    
                    <ul class="bullet-list">
                        <li>A custom AloAi Agent is active on your account</li>
                        <li>Missed call settings for all Lines and Inboxes are routed to and answered by Call Rescue</li>
                        <li>Usage is billed at <strong class="price-text">${{ getBasicTierPrice() }}/min</strong> for basic voice models</li>
                    </ul>
                    
                    <el-button
                        type="primary"
                        size="small"
                        @click="goToAloAiList"
                        class="mt-2 mb-3"
                    >
                        Go to the AI Call Rescue
                    </el-button>
                    
                    <p class="content-text">
                        Disable to revert all missed call actions to their previous settings.
                    </p>
                    <p class="content-text">
                        You can re-enable it anytime.
                    </p>
                </div>
                
                <!-- STATE 2: NO_CONSENT - No consent exists -->
                <div v-if="handlerState === $options.HANDLER_STATE_NO_CONSENT">
                    <p class="content-text">
                        <strong>Let AloAi answer missed calls</strong>
                    </p>
                    
                    <p class="content-text">
                        Enable AI Call Rescue to automatically follow up with callers when your team is unavailable.
                    </p>
                    
                    <ul class="bullet-list">
                        <li>A new AloAi Agent is created and enabled by default</li>
                        <li>Missed call settings for all Lines and Inboxes are updated to use this Agent</li>
                        <li>All calls handled by AI Call Rescue are automatically recorded and transcribed</li>
                        <li>Your previous settings are preserved and can be restored at any time</li>
                        <li>Billing starts at <strong class="price-text">${{ getBasicTierPrice() }}/min</strong> for basic voice models</li>
                    </ul>
                    
                    <p class="content-text mt-3">
                        <strong>Requires acceptance before activation.</strong>
                    </p>
                    <p class="content-text">
                        You can disable it anytime to revert all missed call actions to their previous settings.
                    </p>
                    
                    <el-button
                        type="success"
                        size="medium"
                        @click="handleConsentClick"
                        :loading="loading"
                        class="mt-2"
                    >
                        Accept & Enable
                    </el-button>
                </div>
                
                <!-- STATE 3: DISABLED - Consent accepted but feature is disabled -->
                <div v-if="handlerState === $options.HANDLER_STATE_DISABLED">
                    <p class="content-text">
                        <strong>AI Call Rescue is currently disabled</strong>
                    </p>
                    
                    <p class="content-text">
                        You've previously accepted and enabled AI Call Rescue, but it's off right now.
                    </p>
                    
                    <ul class="bullet-list">
                        <li>All lines reverted to their previous missed call settings</li>
                        <li>Re-enable anytime to let AI Call Rescue answer all missed calls</li>
                        <li>Billing resumes at <strong class="price-text">${{ getBasicTierPrice() }}/min</strong> for basic voice models</li>
                    </ul>
                </div>
            </div>
        </el-card>
    </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import * as Consent from '../../constants/consent';

// Handler state constants
const HANDLER_STATE_ENABLED = 'ENABLED'
const HANDLER_STATE_NO_CONSENT = 'NO_CONSENT'
const HANDLER_STATE_DISABLED = 'DISABLED'

export default {
    name: 'MissedCallHandlerSettings',
    
    HANDLER_STATE_ENABLED,
    HANDLER_STATE_NO_CONSENT,
    HANDLER_STATE_DISABLED,
    
    data() {
        return {
            loading: false,
            enabled: false,
            consent: {
                has_consent: false,
                consent_decision: null,
                consent_date: null,
                consent_type: null,
                show_modal: false,
                is_applicable: false
            }
        }
    },
    
    computed: {
        ...mapState('cache', ['current_company']),
        
        /**
         * Determines the current state of the Call Rescue feature
         * State 1 (ENABLED): Consent is accepted AND feature is enabled
         * State 2 (NO_CONSENT): No consent exists (neither accepted nor denied) - Default
         * State 3 (DISABLED): Consent is accepted BUT feature is disabled
         */
        handlerState() {
            const hasAcceptedConsent = this.consent.has_consent && this.consent.consent_decision === 'accepted'
            
            if (hasAcceptedConsent && this.enabled) {
                return HANDLER_STATE_ENABLED
            }
            
            if (hasAcceptedConsent && !this.enabled) {
                return HANDLER_STATE_DISABLED
            }
            
            return HANDLER_STATE_NO_CONSENT
        }
    },

    mounted() {
        this.loadSettings()
    },
    
    watch: {
        'consent.is_applicable': {
            handler(newValue) {
                this.$emit('applicability-changed', newValue)
            },
            immediate: true
        }
    },
    
    methods: {
        ...mapActions('cache', ['setCurrentCompany']),
    
        async updateCurrentCompanyDetails() {
            const response = await axios.get(`/api/v1/company/${this.current_company.id}`)
            this.setCurrentCompany(response.data)
        },
        
        getBasicTierPrice() {
            const company = this.current_company
            const rate = company?.rate
            
            if (rate?.aloai_voice_bot_basic_minute) {
                return parseFloat(rate.aloai_voice_bot_basic_minute).toFixed(2)
            }
            
            return '0.10'
        },
        
        async loadSettings() {
            this.loading = true
            
            try {
                this.enabled = this.current_company.aloai_missed_call_handler_enabled || false
                
                const consentResponse = await axios.get(`/api/v1/consent/${Consent.CONSENT_TYPE_MISSED_CALL_HANDLER}`)
                this.consent = consentResponse.data
            } catch (error) {
            } finally {
                this.loading = false
            }
        },
        
        async handleToggle() {
            this.loading = true
            
            try {
                await axios.patch('/api/v1/company/flag', {
                    flag_name: 'aloai_missed_call_handler_enabled',
                    enabled: this.enabled
                })
                
                await this.updateCurrentCompanyDetails()
                
                this.$notify({
                    title: 'Settings',
                    message: this.enabled ? 'AI Call Rescue enabled for your account.' : 'Call Rescue remains off.',
                    type: 'success'
                })
            } catch (error) {
                this.$root.handleErrors(error.response)
                this.enabled = !this.enabled
            } finally {
                this.loading = false
            }
        },
        
        async handleConsentClick() {
            this.loading = true
            
            try {
                await axios.post(`/api/v1/consent/${Consent.CONSENT_TYPE_MISSED_CALL_HANDLER}`, {
                    accepted: true
                })
                
                await this.updateCurrentCompanyDetails()
                await this.loadSettings()
                
                this.$notify({
                    title: 'Consent',
                    message: 'AI Call Rescue enabled for your account.',
                    type: 'success'
                })
            } catch (error) {
                this.$root.handleErrors(error.response)
            } finally {
                this.loading = false
            }
        },
        
        goToAloAiList() {
            // Include show_system_bots=true to display the system-managed Call Rescue bot
            this.$router.push({ name: 'AloAi Agents', query: { show_system_bots: true } })
        }
    }
}
</script>

<style scoped>
.missed-call-handler-settings {
    max-width: 1000px;
}

.card-header {
    padding: 0;
    margin-bottom: 20px;
    background: transparent;
}

.card-content {
    padding: 0;
}

.content-text {
    margin-bottom: 15px;
    font-size: 14px;
    line-height: 1.5;
    color: #606266;
}

.content-text:last-child {
    margin-bottom: 0;
}

.content-text a {
    text-decoration: underline;
}

.content-text strong {
    font-size: 14px !important;
    font-weight: bold;
}

.text-primary {
    color: #409EFF !important;
}

.info-row {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 15px;
}

.info-primary {
    color: #303133;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
}

.info-secondary {
    color: #909399;
    font-size: 12px;
    margin: 0;
}

.disabled-message {
    color: #606266;
    font-size: 14px;
    background: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 15px;
}

.bullet-list {
    margin: 15px 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 1.8;
    color: #606266;
}

.bullet-list li {
    margin-bottom: 8px;
}

.bullet-list li:last-child {
    margin-bottom: 0;
}

.price-text {
    font-size: 14px !important;
}
</style>
